import { useEffect, useState } from "react";
import { literals } from "@/constants/literals";
import {
  getImageDeletionRequestsByStatus,
  processImageDeletionRequest,
} from "@/lib/services/images";
import { appToast } from "@/lib/toast";
import { useLoader } from "@/store/useLoader";
import { ImageDeletionRequestStatus } from "@/constants/constants";
import RequestsDetailMobile from "./RequestsDetailMobile";
import RequestCardMobile from "./RequestsCardMobile";
import RequestsFiltersMobile from "./RequestsFiltersMobile";
import type { ImageDeletionRequestApi, PetitionsParams } from "@/types/types.d";
import Pagination from "@/components/Pagination/Pagination";
import { useUserStore } from "@/store/useUser";

type RequestFilters = {
  area: string;
};

const RequestsMobile = () => {
  const { upLoader, downLoader } = useLoader();
  const { isSupervisor } = useUserStore();

  const [requests, setRequests] = useState<ImageDeletionRequestApi[]>([]);
  const [selectedRequest, setSelectedRequest] =
    useState<ImageDeletionRequestApi | null>(null);
  const [loading, setLoading] = useState(true);
  const [openFilters, setOpenFilters] = useState(false);

  const [filters, setFilters] = useState<RequestFilters>({
    area: "",
  });
  const [page, setPage] = useState(0);
  const [lastPage, setLastPage] = useState(0);

  const loadRequests = async (
    activeFilters: RequestFilters = filters,
    newPage: number = page,
  ) => {
    try {
      setLoading(true);

      const params: PetitionsParams = {
        status: "PENDING",
        type: "DELETE",
        page: newPage,
      };
      if (activeFilters?.area) params.area = activeFilters.area;

      const data = await getImageDeletionRequestsByStatus(params);

      setRequests(data.content);
      setPage(data?.pageable?.pageNumber || 0);
      setLastPage(data?.totalPages || 0);
    } catch (e) {
      console.error(e);
      appToast.error(
        literals.aProblem,
        "No se han podido cargar las solicitudes",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests(filters);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenDetail = (request: ImageDeletionRequestApi) => {
    setSelectedRequest(request);
  };

  const handleCloseDetail = () => {
    setSelectedRequest(null);
  };

  const handleApplyFilters = async (newFilters: RequestFilters) => {
    setFilters(newFilters);
    setOpenFilters(false);
    await loadRequests(newFilters, 0);
  };

  const handleProcess = async (status: "APPROVED" | "REJECTED") => {
    if (!isSupervisor) {
      appToast.error(literals.aProblem, literals.notAllowed);
      return;
    }

    if (!selectedRequest) return;

    try {
      upLoader();

      await processImageDeletionRequest(selectedRequest.requestId, status);

      const fileName =
        selectedRequest.s3Key.split("/").pop() ?? selectedRequest.s3Key;

      if (status === ImageDeletionRequestStatus.APPROVED) {
        appToast.success(literals.success, `La foto ${fileName} se ha borrado`);
      } else {
        appToast.success(
          literals.success,
          `Se ha rechazado el borrado de la foto ${fileName}`,
        );
      }

      setSelectedRequest(null);
      await loadRequests(filters);
    } catch (e) {
      console.error(e);
      appToast.error(
        "Ha habido un problema",
        "No se ha podido procesar la solicitud",
      );
    } finally {
      downLoader();
    }
  };
  const changePage = async (newPage: number) => {
    await loadRequests(filters, newPage);
  };

  if (selectedRequest) {
    return (
      <RequestsDetailMobile
        request={selectedRequest}
        cleanRequest={handleCloseDetail}
        onProcess={handleProcess}
      />
    );
  }

  if (openFilters) {
    return (
      <RequestsFiltersMobile
        initialFilters={filters}
        onApply={handleApplyFilters}
        onClose={() => setOpenFilters(false)}
      />
    );
  }

  return (
    <div className="pb-4">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-lg font-ib-bold">{literals.respondRequest}</h1>

        <button
          type="button"
          onClick={() => setOpenFilters(true)}
          className="rounded-full border border-[#D9D9D9] bg-white px-4 py-2 text-sm font-medium text-[#333333] active:scale-[0.98]"
        >
          Filtrar
        </button>
      </div>

      {loading && (
        <div className="py-8 text-center text-sm text-gray-500">
          Cargando solicitudes...
        </div>
      )}

      {!loading && requests.length === 0 && (
        <div className="py-8 text-center text-sm text-gray-500">
          No hay solicitudes pendientes
        </div>
      )}

      {!loading && requests.length > 0 ? (
        <div className="grid gap-2">
          {requests.map((request, index) => (
            <RequestCardMobile
              key={`${request.requestId}-${index}`}
              item={request}
              onClick={handleOpenDetail}
            />
          ))}
        </div>
      ) : null}
      {!loading && requests.length > 1 ? (
        <div className="py-4">
          <Pagination
            lastPage={lastPage}
            currentPage={page + 1}
            changePage={changePage}
          />
        </div>
      ) : null}
    </div>
  );
};

export default RequestsMobile;
